import { useRef, useCallback, useState, useEffect } from 'react';
import { getEventClientPosition, calculateValueByPosition } from './utils';
import {
  SlideEvent,
  ReactMouseEvent,
  ReactSlideEvent,
  InputSliderProps
} from './types';

export function useSlide(props: Partial<InputSliderProps>) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const handlerRef = useRef<HTMLDivElement>(null);
  const [sliding, setSliding] = useState(false);
  const startRef = useRef(0);
  const offsetRef = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;

  const handleChange = useCallback((position: number, cliced = false) => {
    const onChange = cliced
      ? propsRef.current.onClickChange
      : propsRef.current.onChange;

    if (!onChange) {
      return;
    }

    const { vertical, min, max, step } = propsRef.current;
    const { width, height } = sliderRef.current!.getBoundingClientRect();
    const bound = vertical ? height : width;
    onChange(
      calculateValueByPosition(position, vertical!, min!, max!, step!, bound)
    );
  }, []);

  const onSliderClick = useCallback((event: ReactMouseEvent) => {
    const vertical = propsRef.current.vertical!;
    const { x, y } = getEventClientPosition(event);
    const { left, top } = sliderRef.current!.getBoundingClientRect();
    const position = vertical ? y - top : x - left;
    handleChange(position, true);
  }, []);

  const onSlideStart = useCallback((event: ReactSlideEvent) => {
    event.preventDefault();

    const { vertical, value, onBeforeChange } = propsRef.current;
    const { offsetLeft, offsetTop } = handlerRef.current!;
    const { x, y } = getEventClientPosition(event);

    startRef.current = vertical ? offsetTop : offsetLeft;
    offsetRef.current = vertical ? y : x;

    setSliding(true);

    if (onBeforeChange) {
      onBeforeChange(value!);
    }
  }, []);

  useEffect(() => {
    if (!sliding) {
      return;
    }

    function handleDrag(event: SlideEvent) {
      event.preventDefault();
      const { vertical } = propsRef.current;
      const { x, y } = getEventClientPosition(event);
      const clientPosition = vertical ? y : x;
      const position = startRef.current + (clientPosition - offsetRef.current);
      handleChange(position);
    }

    function handleDragEnd(event: SlideEvent) {
      event.preventDefault();
      const { value, onAfterChange } = propsRef.current;
      setSliding(false);

      if (onAfterChange) {
        onAfterChange(value!);
      }
    }

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDrag, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
    document.addEventListener('touchcancel', handleDragEnd);

    return () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDrag);
      document.removeEventListener('touchend', handleDragEnd);
      document.removeEventListener('touchcancel', handleDragEnd);
    };
  }, [sliding]);

  return {
    sliderRef,
    handlerRef,
    onSliderClick,
    onSlideStart
  };
}
